import React, { useState } from "react";
import { toast } from "react-toastify";
import { CheckCircle2, Crown, Headset, Loader2, Mail, Scale, MessagesSquare } from "lucide-react";
import { abrirChatEliteConTexto, abrirTicketChatElite } from "../../../utilities/chatEliteAbrir";
import { puedeUsarWidgetChatElite, CHAT_ELITE_VISIBLE } from "../../../utilities/chatEliteUsuario";
import { crearTicketChatElite } from "../../../services/chatElite.service";
import { marcarTicketDerivacion } from "../../../services/asistente.service";
import "./LAEAI.css";

/**
 * Botón bajo una respuesta derivada a otro canal (lo decide el back con la herramienta derivar_consulta):
 * - soporte + plan Élite: crea la consulta en el Chat de LegalIA con la pregunta (ya enviada), la abre y la apunta en
 *   el historial (ticket_id) para que después el botón sea "Ver consulta" y no se dupliquen consultas.
 *   El texto de la consulta es el mensaje que redacta la IA (derivacion.mensaje) o, si no hay, la pregunta.
 * - soporte sin Élite: abre un correo a soporte (asunto y mensaje de la IA, con saludo y firma) y otro botón para pedir
 *   al comercial el plan Élite (correo predefinido).
 * - juridico: "Chat Jurídico" (próximamente), sin acción.
 */
const EMAIL_SOPORTE = "soporte.plataforma@grupolae.net";

const mailto = (para, asunto, cuerpo) =>
  `mailto:${para}?subject=${encodeURIComponent(asunto)}&body=${encodeURIComponent(cuerpo)}`;

// Firma del correo: persona de contacto, razón social y NIF del administrador
const firmaDe = (user) =>
  [user?.nombreContacto, user?.nombre, user?.nif ? `NIF ${user.nif}` : null].filter(Boolean).join("\n");

// Comercial del AAFF (en el token): { email, nombre }. Sin comercial, el correo va a soporte
const comercialDe = (user) => {
  const c = user?.comerciales;
  const email = c?.user?.[0]?.email || c?.user?.email;
  return email ? { email, nombre: c?.nombre || "" } : { email: EMAIL_SOPORTE, nombre: "" };
};

const clasesBoton = "inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition";
// Mismo negro que el botón flotante del Chat de LegalIA (--ce-tinta: #111) y su hover (opacidad 0.88)
const clasesNegro = "border-[#111] bg-[#111] text-white";

export default function LAEAIDerivacion({ derivacion, conversacionId, mensajeId, onActualizar, user, t }) {
  const [enviando, setEnviando] = useState(false);
  if (!derivacion?.destino) return null;

  // Con el Chat de LegalIA oculto, a quien no tiene Élite no se le muestra nada (la IA le dice que hable con su gestor)
  if (derivacion.destino !== "juridico" && !CHAT_ELITE_VISIBLE && user?.legalia !== "Elite") return null;
  // Chat Jurídico, y el Chat de LegalIA mientras está oculto: "próximamente", sin acción
  const proximamente = derivacion.destino === "juridico" || !CHAT_ELITE_VISIBLE;
  if (proximamente) {
    const juridico = derivacion.destino === "juridico";
    return (
      <span className={`${clasesBoton} cursor-default border-gray-200 bg-gray-50 text-gray-500`} aria-disabled="true">
        {juridico ? <Scale size={16} /> : <MessagesSquare size={16} />}
        {t(juridico ? "laeai.derivacion.juridico" : "laeai.derivacion.chatLegalia")}
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
          {t("laeai.derivacion.proximamente")}
        </span>
      </span>
    );
  }

  if (puedeUsarWidgetChatElite(user)) {
    // Ya enviada: abrir esa consulta
    if (derivacion.ticket_id) {
      return (
        <span className="inline-flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
            <CheckCircle2 size={14} /> {t("laeai.derivacion.enviada")}
          </span>
          <button
            type="button"
            onClick={() => abrirTicketChatElite({ id: derivacion.ticket_id })}
            className={`${clasesBoton} cursor-pointer border-gray-300 bg-white text-gray-800 hover:bg-gray-50`}
          >
            <Headset size={16} />
            {t("laeai.derivacion.ver")}
          </button>
        </span>
      );
    }

    const enviar = async () => {
      if (enviando) return;
      setEnviando(true);
      try {
        const { ticket } = await crearTicketChatElite({ texto: derivacion.mensaje || derivacion.consulta });
        abrirTicketChatElite(ticket);
        const actualizada = { ...derivacion, ticket_id: ticket.id };
        onActualizar?.(actualizada);
        // Apuntarlo en el historial (si falla, la consulta ya está creada y abierta)
        if (conversacionId && mensajeId) {
          marcarTicketDerivacion(conversacionId, mensajeId, ticket.id).catch(() => {});
        }
      } catch (error) {
        toast.error(t(`chatElite.errores.${error.code}`, { defaultValue: t("chatElite.errores.ERROR") }));
        // Como alternativa, abrir "Nueva consulta" con la pregunta escrita para que la envíe a mano
        abrirChatEliteConTexto(derivacion.mensaje || derivacion.consulta);
      } finally {
        setEnviando(false);
      }
    };

    return (
      <button
        type="button"
        onClick={enviar}
        disabled={enviando}
        className={`${clasesBoton} ${clasesNegro} ${enviando ? "cursor-wait opacity-80" : "cursor-pointer hover:opacity-[0.88]"}`}
      >
        {enviando ? <Loader2 size={16} className="animate-spin" /> : <Headset size={16} />}
        {t("laeai.derivacion.chat")}
      </button>
    );
  }

  // Sin plan Élite: correo a soporte con el mensaje de la IA (saludo y firma fijos) y solicitud del plan al comercial
  const firma = firmaDe(user);
  const correoSoporte = mailto(
    EMAIL_SOPORTE,
    derivacion.asunto || t("laeai.derivacion.asunto"),
    t("laeai.derivacion.correoSoporte", { mensaje: derivacion.mensaje || derivacion.consulta || "", firma }),
  );
  const comercial = comercialDe(user);
  const correoContratar = mailto(
    comercial.email,
    t("laeai.derivacion.contratarAsunto"),
    t("laeai.derivacion.contratarCuerpo", { nombre: comercial.nombre ? `, ${comercial.nombre}` : "", firma }),
  );
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <a href={correoSoporte} className={`${clasesBoton} laeai-derivacion ${clasesNegro} hover:opacity-[0.88]`}>
        <Mail size={16} />
        {t("laeai.derivacion.contactar")}
      </a>
      <a href={correoContratar} className={`${clasesBoton} laeai-derivacion laeai-derivacion--clara border-gray-300 bg-white hover:bg-gray-50`}>
        <Crown size={16} />
        {t("laeai.derivacion.contratar")}
      </a>
    </span>
  );
}
